import React, { FormEvent, useMemo, useState } from "react";
import {
  ActivityTemplateEditableFields,
  PortalActivityImage,
  PortalActivityTemplateDetail,
  PortalActivityType,
} from "../api/contracts";
import { ClinicianPortalApiError } from "../api/clinicianPortalApi";
import { templateStepsEditorPath } from "../config/activityStepsNavigation";
import { getActivityTypePillToken } from "../config/palette";
import { formatPortalDateTime } from "../utils/dateTime";
import { PortalLink } from "./PortalLink";
import { SingleSelectPillField } from "./SingleSelectPillField";
import {
  BooleanIndicator,
  ContentSection,
  RecordDetailCard,
  RecordFieldRow,
} from "./DataDisplay";
import { ActivityImageUploadField } from "./ActivityImages";

interface ActivityTemplateFormErrors {
  name?: string;
  activityTypeId?: string;
  points?: string;
  coins?: string;
}

export const emptyActivityTemplateFormValues: ActivityTemplateEditableFields = {
  name: "",
  description: "",
  activityTypeId: "",
  status: "On",
  nonExposureHomework: false,
  activitySteps: "",
  alternative: "",
  coachingPrompts: "",
  points: 0,
  coins: 0,
};

export function getSelectableActivityTypes(
  activityTypes: readonly PortalActivityType[],
): PortalActivityType[] {
  return activityTypes
    .filter((activityType) => activityType.enabled)
    .sort(
      (left, right) =>
        left.sortOrder - right.sortOrder ||
        left.name.localeCompare(right.name) ||
        left.id.localeCompare(right.id),
    );
}

function submissionErrorMessage(error: unknown): string | undefined {
  if (!error) return undefined;
  if (!(error instanceof ClinicianPortalApiError)) {
    return "Activity Template could not be saved. Try again.";
  }
  if (error.code === "NOT_AUTHORIZED" || error.code === "NOT_FOUND") {
    return "This Activity Template is unavailable.";
  }
  if (error.code === "INVALID_REQUEST") {
    return error.message || "Review the form values and try again.";
  }
  return "Activity Template could not be saved. Try again.";
}

type ActivityTemplateHelpField =
  | "nonExposureHomework"
  | "activitySteps"
  | "alternative"
  | "coachingPrompts"
  | "activityImages";

const activityTemplateHelp: Record<ActivityTemplateHelpField, React.ReactNode> = {
  nonExposureHomework:
    "When checked, the assistant will skip asking for SUDS ratings, coping skills used, and any altered steps for this homework.",
  activitySteps: (
    <>
      Number each step and write clear, concrete instructions for what the client
      should do.
      <br />
      Each numbered step must be on its own line. Do not combine multiple steps on
      the same line.
    </>
  ),
  alternative: (
    <>
      Provide 1–3{" "}
      <strong>
        approved modifications of the <em>same</em> activity steps
      </strong>{" "}
      to use when the client asks to make it easier, modify, or switch. Enter each{" "}
      <strong>modified step on a separate line</strong> (bullets ok: * or -).
    </>
  ),
  coachingPrompts: (
    <>
      Use coaching prompts as client coaching tips and/or parent scripts during{" "}
      <strong>timed</strong> activity steps.
    </>
  ),
  activityImages:
    "To display an image in an activity step, first upload the image file. Then add image: image_name at the end of the step where the image should appear. For example, image: toothpaste_amount.",
};

function ActivityTemplateFieldHelp({
  field,
}: {
  field: ActivityTemplateHelpField;
}) {
  return (
    <p className="cp-form-help cp-activity-template-form-help">
      {activityTemplateHelp[field]}
    </p>
  );
}

export function ActivityTemplateForm({
  mode,
  initialValues,
  activityTypes,
  clinicianCode,
  cancelPath,
  isSubmitting,
  submissionError,
  clearSubmissionError,
  onSubmit,
  template,
  onUploadImage,
  onRemoveImage,
}: {
  mode: "create" | "edit";
  initialValues: ActivityTemplateEditableFields;
  activityTypes: readonly PortalActivityType[];
  clinicianCode: string;
  cancelPath: string;
  isSubmitting: boolean;
  submissionError?: unknown;
  clearSubmissionError: () => void;
  onSubmit: (values: ActivityTemplateEditableFields) => Promise<void>;
  template?: PortalActivityTemplateDetail;
  onUploadImage?: (file: File) => Promise<PortalActivityImage>;
  onRemoveImage?: (imageId: string) => Promise<void>;
}) {
  const [values, setValues] = useState(initialValues);
  const [pointsInput, setPointsInput] = useState(String(initialValues.points));
  const [coinsInput, setCoinsInput] = useState(String(initialValues.coins));
  const [validationErrors, setValidationErrors] =
    useState<ActivityTemplateFormErrors>({});
  const [activityImages, setActivityImages] = useState<PortalActivityImage[]>(
    template?.activityImages ?? [],
  );
  const selectableActivityTypes = useMemo(
    () => getSelectableActivityTypes(activityTypes),
    [activityTypes],
  );
  const selectedActivityType = activityTypes.find(
    (activityType) => activityType.id === values.activityTypeId,
  );
  const selectedDisabledActivityType =
    selectedActivityType && !selectedActivityType.enabled
      ? selectedActivityType
      : undefined;
  const activityTypePillOptions = useMemo(
    () => [
      ...(selectedDisabledActivityType
        ? [
            {
              id: selectedDisabledActivityType.id,
              value: selectedDisabledActivityType.id,
              label: selectedDisabledActivityType.name,
              colorKey: selectedDisabledActivityType.colorKey,
              disabled: true,
            },
          ]
        : []),
      ...selectableActivityTypes.map((activityType) => ({
        id: activityType.id,
        value: activityType.id,
        label: activityType.name,
        colorKey: activityType.colorKey,
      })),
    ],
    [selectableActivityTypes, selectedDisabledActivityType],
  );
  const apiError = submissionErrorMessage(submissionError);

  const clearErrors = (field?: keyof ActivityTemplateFormErrors) => {
    setValidationErrors((current) => {
      if (!field || !current[field]) return field ? current : {};
      const next = { ...current };
      delete next[field];
      return next;
    });
    if (!isSubmitting) clearSubmissionError();
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (isSubmitting) return;

    const normalizedName = values.name.trim();
    const points = Number(pointsInput);
    const coins = Number(coinsInput);
    const nextErrors: ActivityTemplateFormErrors = {};
    if (!normalizedName) {
      nextErrors.name = "Activity Template is required.";
    }
    if (!values.activityTypeId) {
      nextErrors.activityTypeId = "Activity Type is required.";
    }
    if (pointsInput.trim() === "" || !Number.isInteger(points)) {
      nextErrors.points = "Activity Points must be an integer.";
    }
    if (coinsInput.trim() === "" || !Number.isInteger(coins)) {
      nextErrors.coins = "Activity Coins must be an integer.";
    }

    setValidationErrors(nextErrors);
    clearSubmissionError();
    if (Object.keys(nextErrors).length) return;

    await onSubmit({
      ...values,
      name: normalizedName,
      points,
      coins,
    });
  };

  if (mode === "edit" && template) {
    return (
      <form
        className="cp-activity-template-edit-form"
        onSubmit={handleSubmit}
        noValidate
        aria-label="Edit Activity Template"
      >
        <ContentSection title="Template Profile">
          <RecordDetailCard>
            <RecordFieldRow label="Activity Template">
              <label
                className="cp-visually-hidden"
                htmlFor="edit-activity-template-name"
              >
                Activity Template
              </label>
              <input
                id="edit-activity-template-name"
                className="cp-form-input"
                type="text"
                value={values.name}
                required
                aria-invalid={Boolean(validationErrors.name)}
                aria-describedby={
                  validationErrors.name
                    ? "edit-activity-template-name-error"
                    : undefined
                }
                onChange={(event) => {
                  clearErrors("name");
                  setValues((current) => ({
                    ...current,
                    name: event.target.value,
                  }));
                }}
              />
              {validationErrors.name ? (
                <p
                  className="cp-form-error"
                  id="edit-activity-template-name-error"
                >
                  {validationErrors.name}
                </p>
              ) : null}
            </RecordFieldRow>

            <RecordFieldRow label="Description">
              <label
                className="cp-visually-hidden"
                htmlFor="edit-activity-template-description"
              >
                Description
              </label>
              <textarea
                id="edit-activity-template-description"
                className="cp-form-input cp-form-textarea"
                rows={4}
                value={values.description}
                onChange={(event) => {
                  clearErrors();
                  setValues((current) => ({
                    ...current,
                    description: event.target.value,
                  }));
                }}
              />
            </RecordFieldRow>

            <RecordFieldRow label="Activity Type">
              <SingleSelectPillField
                id="edit-activity-template-type"
                value={values.activityTypeId}
                options={activityTypePillOptions}
                onChange={(activityTypeId) => {
                  clearErrors("activityTypeId");
                  setValues((current) => ({ ...current, activityTypeId }));
                }}
                ariaLabel="Choose an Activity Type"
                ariaDescribedBy={
                  validationErrors.activityTypeId
                    ? "edit-activity-template-type-error"
                    : undefined
                }
                placeholder="Select an Activity Type"
                searchPlaceholder="Find an Activity Type"
                emptyMessage="No Activity Types found"
                resolveColor={(option) =>
                  getActivityTypePillToken(option.label, option.colorKey)
                }
                disabled={isSubmitting}
                invalid={Boolean(validationErrors.activityTypeId)}
              />
              {validationErrors.activityTypeId ? (
                <p
                  className="cp-form-error"
                  id="edit-activity-template-type-error"
                >
                  {validationErrors.activityTypeId}
                </p>
              ) : null}
            </RecordFieldRow>

            <RecordFieldRow label="Status">
              <label
                className="cp-visually-hidden"
                htmlFor="edit-activity-template-status"
              >
                Status
              </label>
              <select
                id="edit-activity-template-status"
                className="cp-form-input"
                value={values.status}
                onChange={(event) => {
                  clearErrors();
                  setValues((current) => ({
                    ...current,
                    status: event.target
                      .value as ActivityTemplateEditableFields["status"],
                  }));
                }}
              >
                <option value="On">On</option>
                <option value="Off">Off</option>
              </select>
            </RecordFieldRow>

            <RecordFieldRow label="Non-Exposure Homework">
              <ActivityTemplateFieldHelp field="nonExposureHomework" />
              <label className="cp-form-checkbox cp-activity-template-edit-checkbox">
                <input
                  type="checkbox"
                  checked={values.nonExposureHomework}
                  onChange={(event) => {
                    clearErrors();
                    setValues((current) => ({
                      ...current,
                      nonExposureHomework: event.target.checked,
                    }));
                  }}
                />
                <span className="cp-visually-hidden">
                  Non-Exposure Homework
                </span>
              </label>
            </RecordFieldRow>

            <RecordFieldRow label="Activity Points">
              <label
                className="cp-visually-hidden"
                htmlFor="edit-activity-template-points"
              >
                Activity Points
              </label>
              <input
                id="edit-activity-template-points"
                className="cp-form-input cp-form-input--no-spinner cp-activity-template-edit-number"
                type="number"
                step="1"
                value={pointsInput}
                aria-invalid={Boolean(validationErrors.points)}
                aria-describedby={
                  validationErrors.points
                    ? "edit-activity-template-points-error"
                    : undefined
                }
                onChange={(event) => {
                  clearErrors("points");
                  setPointsInput(event.target.value);
                }}
              />
              {validationErrors.points ? (
                <p
                  className="cp-form-error"
                  id="edit-activity-template-points-error"
                >
                  {validationErrors.points}
                </p>
              ) : null}
            </RecordFieldRow>

            <RecordFieldRow label="Activity Coins">
              <label
                className="cp-visually-hidden"
                htmlFor="edit-activity-template-coins"
              >
                Activity Coins
              </label>
              <input
                id="edit-activity-template-coins"
                className="cp-form-input cp-form-input--no-spinner cp-activity-template-edit-number"
                type="number"
                step="1"
                value={coinsInput}
                aria-invalid={Boolean(validationErrors.coins)}
                aria-describedby={
                  validationErrors.coins
                    ? "edit-activity-template-coins-error"
                    : undefined
                }
                onChange={(event) => {
                  clearErrors("coins");
                  setCoinsInput(event.target.value);
                }}
              />
              {validationErrors.coins ? (
                <p
                  className="cp-form-error"
                  id="edit-activity-template-coins-error"
                >
                  {validationErrors.coins}
                </p>
              ) : null}
            </RecordFieldRow>

            <RecordFieldRow label="Protected">
              <BooleanIndicator value={template.protected} />
            </RecordFieldRow>
            <RecordFieldRow label="Created">
              {formatPortalDateTime(template.createdAt)}
            </RecordFieldRow>
            <RecordFieldRow label="Updated">
              {formatPortalDateTime(template.updatedAt)}
            </RecordFieldRow>
          </RecordDetailCard>
        </ContentSection>

        <ContentSection title="Activity Content">
          <RecordDetailCard>
            <RecordFieldRow label="Activity Steps">
              <ActivityTemplateFieldHelp field="activitySteps" />
              <div className="cp-detail-value-with-action">
                <label
                  className="cp-visually-hidden"
                  htmlFor="edit-activity-template-steps"
                >
                  Activity Steps
                </label>
                <textarea
                  id="edit-activity-template-steps"
                  className="cp-form-input cp-form-textarea cp-activity-template-steps-editor"
                  rows={7}
                  value={values.activitySteps}
                  onChange={(event) => {
                    clearErrors();
                    setValues((current) => ({
                      ...current,
                      activitySteps: event.target.value,
                    }));
                  }}
                />
                {mode === "edit" && template ? (
                  <a
                    className="cp-edit-steps-button"
                    href={templateStepsEditorPath({
                      clinicianCode,
                      templateId: template.id,
                      returnUrl: window.location.href,
                    })}
                  >
                    Edit Steps
                  </a>
                ) : null}
              </div>
            </RecordFieldRow>
            <RecordFieldRow label="Modification Steps">
              <ActivityTemplateFieldHelp field="alternative" />
              <label
                className="cp-visually-hidden"
                htmlFor="edit-activity-template-alternative"
              >
                Modification Steps
              </label>
              <textarea
                id="edit-activity-template-alternative"
                className="cp-form-input cp-form-textarea"
                rows={5}
                value={values.alternative}
                onChange={(event) => {
                  clearErrors();
                  setValues((current) => ({
                    ...current,
                    alternative: event.target.value,
                  }));
                }}
              />
            </RecordFieldRow>
            <RecordFieldRow label="Coaching Prompts">
              <ActivityTemplateFieldHelp field="coachingPrompts" />
              <label
                className="cp-visually-hidden"
                htmlFor="edit-activity-template-coaching-prompts"
              >
                Coaching Prompts
              </label>
              <textarea
                id="edit-activity-template-coaching-prompts"
                className="cp-form-input cp-form-textarea"
                rows={5}
                value={values.coachingPrompts}
                onChange={(event) => {
                  clearErrors();
                  setValues((current) => ({
                    ...current,
                    coachingPrompts: event.target.value,
                  }));
                }}
              />
            </RecordFieldRow>
            {onUploadImage && onRemoveImage ? (
              <RecordFieldRow label="Activity Images">
                <ActivityTemplateFieldHelp field="activityImages" />
                <ActivityImageUploadField
                  images={activityImages}
                  disabled={isSubmitting}
                  onUpload={async (file) => {
                    const image = await onUploadImage(file);
                    setActivityImages((current) =>
                      [...current, image].sort(
                        (left, right) =>
                          left.sortOrder - right.sortOrder ||
                          left.filename.localeCompare(right.filename),
                      ),
                    );
                  }}
                  onRemove={async (imageId) => {
                    await onRemoveImage(imageId);
                    setActivityImages((current) =>
                      current.filter((image) => image.id !== imageId),
                    );
                  }}
                />
              </RecordFieldRow>
            ) : null}
          </RecordDetailCard>
        </ContentSection>

        {apiError ? (
          <p className="cp-form-error cp-form-error--panel" role="alert">
            {apiError}
          </p>
        ) : null}

        <div className="cp-form-actions cp-form-actions--sticky">
          <PortalLink
            className="cp-button cp-button--secondary"
            path={cancelPath}
            clinicianCode={clinicianCode}
          >
            Cancel
          </PortalLink>
          <button
            type="submit"
            className="cp-button cp-button--primary"
            disabled={isSubmitting || !selectableActivityTypes.length}
          >
            {isSubmitting ? "Saving…" : "Save Changes"}
          </button>
        </div>
      </form>
    );
  }

  return (
    <form
      className="cp-activity-template-edit-form"
      aria-label="Add Activity Template form"
      onSubmit={handleSubmit}
      noValidate
    >
      <ContentSection title="Activity Template Details">
        <p className="cp-content-section__description">
          Create a reusable activity for clinician-managed homework.
        </p>
        <RecordDetailCard>
          <RecordFieldRow label="Activity Template" required>
            <label
              className="cp-visually-hidden"
              htmlFor={`${mode}-activity-template-name`}
            >
              Activity Template
            </label>
            <input
              id={`${mode}-activity-template-name`}
              className="cp-form-input"
              type="text"
              value={values.name}
              required
              aria-invalid={Boolean(validationErrors.name)}
              aria-describedby={
                validationErrors.name
                  ? `${mode}-activity-template-name-error`
                  : undefined
              }
              onChange={(event) => {
                clearErrors("name");
                setValues((current) => ({
                  ...current,
                  name: event.target.value,
                }));
              }}
            />
            {validationErrors.name ? (
              <p
                className="cp-form-error"
                id={`${mode}-activity-template-name-error`}
              >
                {validationErrors.name}
              </p>
            ) : null}
          </RecordFieldRow>

          <RecordFieldRow label="Description">
            <label
              className="cp-visually-hidden"
              htmlFor={`${mode}-activity-template-description`}
            >
              Description
            </label>
            <textarea
              id={`${mode}-activity-template-description`}
              className="cp-form-input cp-form-textarea"
              rows={4}
              value={values.description}
              onChange={(event) => {
                clearErrors();
                setValues((current) => ({
                  ...current,
                  description: event.target.value,
                }));
              }}
            />
          </RecordFieldRow>

          <RecordFieldRow label="Activity Type" required>
            <SingleSelectPillField
              id={`${mode}-activity-template-type`}
              value={values.activityTypeId}
              options={activityTypePillOptions}
              onChange={(activityTypeId) => {
                clearErrors("activityTypeId");
                setValues((current) => ({ ...current, activityTypeId }));
              }}
              ariaLabel="Choose an Activity Type"
              ariaDescribedBy={
                validationErrors.activityTypeId
                  ? `${mode}-activity-template-type-error`
                  : undefined
              }
              placeholder="Select an Activity Type"
              searchPlaceholder="Find an Activity Type"
              emptyMessage="No Activity Types found"
              resolveColor={(option) =>
                getActivityTypePillToken(option.label, option.colorKey)
              }
              disabled={isSubmitting}
              invalid={Boolean(validationErrors.activityTypeId)}
            />
            {validationErrors.activityTypeId ? (
              <p
                className="cp-form-error"
                id={`${mode}-activity-template-type-error`}
              >
                {validationErrors.activityTypeId}
              </p>
            ) : null}
            {!selectableActivityTypes.length ? (
              <p className="cp-form-empty">
                No enabled Activity Types are available.
              </p>
            ) : null}
          </RecordFieldRow>

          <RecordFieldRow label="Status">
            <label
              className="cp-visually-hidden"
              htmlFor={`${mode}-activity-template-status`}
            >
              Status
            </label>
            <select
              id={`${mode}-activity-template-status`}
              className="cp-form-input"
              value={values.status}
              onChange={(event) => {
                clearErrors();
                setValues((current) => ({
                  ...current,
                  status: event.target
                    .value as ActivityTemplateEditableFields["status"],
                }));
              }}
            >
              <option value="On">On</option>
              <option value="Off">Off</option>
            </select>
          </RecordFieldRow>

          <RecordFieldRow label="Non-Exposure Homework">
            <ActivityTemplateFieldHelp field="nonExposureHomework" />
            <label className="cp-form-checkbox cp-activity-template-edit-checkbox">
              <input
                type="checkbox"
                aria-label="Non-Exposure Homework"
                checked={values.nonExposureHomework}
                onChange={(event) => {
                  clearErrors();
                  setValues((current) => ({
                    ...current,
                    nonExposureHomework: event.target.checked,
                  }));
                }}
              />
            </label>
          </RecordFieldRow>

          <RecordFieldRow label="Activity Steps">
            <ActivityTemplateFieldHelp field="activitySteps" />
            <label
              className="cp-visually-hidden"
              htmlFor={`${mode}-activity-template-steps`}
            >
              Activity Steps
            </label>
            <textarea
              id={`${mode}-activity-template-steps`}
              className="cp-form-input cp-form-textarea"
              rows={6}
              value={values.activitySteps}
              onChange={(event) => {
                clearErrors();
                setValues((current) => ({
                  ...current,
                  activitySteps: event.target.value,
                }));
              }}
            />
          </RecordFieldRow>

          <RecordFieldRow label="Modification Steps">
            <ActivityTemplateFieldHelp field="alternative" />
            <label
              className="cp-visually-hidden"
              htmlFor={`${mode}-activity-template-alternative`}
            >
              Modification Steps
            </label>
            <textarea
              id={`${mode}-activity-template-alternative`}
              className="cp-form-input cp-form-textarea"
              rows={5}
              value={values.alternative}
              onChange={(event) => {
                clearErrors();
                setValues((current) => ({
                  ...current,
                  alternative: event.target.value,
                }));
              }}
            />
          </RecordFieldRow>

          <RecordFieldRow label="Coaching Prompts">
            <ActivityTemplateFieldHelp field="coachingPrompts" />
            <label
              className="cp-visually-hidden"
              htmlFor={`${mode}-activity-template-coaching-prompts`}
            >
              Coaching Prompts
            </label>
            <textarea
              id={`${mode}-activity-template-coaching-prompts`}
              className="cp-form-input cp-form-textarea"
              rows={5}
              value={values.coachingPrompts}
              onChange={(event) => {
                clearErrors();
                setValues((current) => ({
                  ...current,
                  coachingPrompts: event.target.value,
                }));
              }}
            />
          </RecordFieldRow>

          <RecordFieldRow label="Activity Images">
            <ActivityTemplateFieldHelp field="activityImages" />
          </RecordFieldRow>

          <RecordFieldRow label="Activity Points">
            <label
              className="cp-visually-hidden"
              htmlFor={`${mode}-activity-template-points`}
            >
              Activity Points
            </label>
            <input
              id={`${mode}-activity-template-points`}
              className="cp-form-input cp-form-input--no-spinner cp-activity-template-edit-number"
              type="number"
              step="1"
              value={pointsInput}
              aria-invalid={Boolean(validationErrors.points)}
              aria-describedby={
                validationErrors.points
                  ? `${mode}-activity-template-points-error`
                  : undefined
              }
              onChange={(event) => {
                clearErrors("points");
                setPointsInput(event.target.value);
              }}
            />
            {validationErrors.points ? (
              <p
                className="cp-form-error"
                id={`${mode}-activity-template-points-error`}
              >
                {validationErrors.points}
              </p>
            ) : null}
          </RecordFieldRow>

          <RecordFieldRow label="Activity Coins">
            <label
              className="cp-visually-hidden"
              htmlFor={`${mode}-activity-template-coins`}
            >
              Activity Coins
            </label>
            <input
              id={`${mode}-activity-template-coins`}
              className="cp-form-input cp-form-input--no-spinner cp-activity-template-edit-number"
              type="number"
              step="1"
              value={coinsInput}
              aria-invalid={Boolean(validationErrors.coins)}
              aria-describedby={
                validationErrors.coins
                  ? `${mode}-activity-template-coins-error`
                  : undefined
              }
              onChange={(event) => {
                clearErrors("coins");
                setCoinsInput(event.target.value);
              }}
            />
            {validationErrors.coins ? (
              <p
                className="cp-form-error"
                id={`${mode}-activity-template-coins-error`}
              >
                {validationErrors.coins}
              </p>
            ) : null}
          </RecordFieldRow>
        </RecordDetailCard>
      </ContentSection>

      {apiError ? (
        <p className="cp-form-error cp-form-error--panel" role="alert">
          {apiError}
        </p>
      ) : null}

      <div className="cp-form-actions cp-form-actions--sticky">
        <PortalLink
          className="cp-button cp-button--secondary"
          path={cancelPath}
          clinicianCode={clinicianCode}
        >
          Cancel
        </PortalLink>
        <button
          type="submit"
          className="cp-button cp-button--primary"
          disabled={isSubmitting || !selectableActivityTypes.length}
        >
          {isSubmitting ? "Creating…" : "Create Template"}
        </button>
      </div>
    </form>
  );
}
